import { Link, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { toast } from "react-toastify";
import MarinerProfileForm from "./MarinerProfileForm";
import ProfileDocuments from "./ProfileDocuments";
import "./MarinerProfileComponent.scss";
import {
  ActionByType,
  ProfileDocumentOperationType,
} from "../../common/ProfileDocument";
import useDocumentTitle from "../../util/useDocumentTitle";
import {
  selectCurrentProfile,
  selectCurrentUser,
} from "../../redux/authentication/authSlice";
import { useSelector } from "react-redux";
import { UserGroup } from "../../common/User";
import { MarinerProfile } from "../../common/MarinerProfile";
import { useGetJobOrdersQuery } from "../../redux/api/jobOrder";
import { JobOrder } from "../../common/JobOrder";
import ProfileTrainings from "./ProfileTrainings";
import ProfileEvaluations from "./ProfileEvaluations";
import ProfileTrainingDocuments from "./ProfileTrainingDocuments";
import ProfileComplianceTrainingDocuments from "./ProfileComplianceTrainingDocuments";
import { TrainingDocumentOperationType } from "../../common/TrainingDocument";
import { useGetMarinerQuery } from "../../redux/api/mariner";
import { skipToken } from "@reduxjs/toolkit/query";

const MarinerProfileComponent = () => {
  useDocumentTitle("Mariner");
  const { id } = useParams();

  const user = useSelector(selectCurrentUser);
  const userGroup = user?.userGroup as UserGroup;
  const userProfile: MarinerProfile = useSelector(selectCurrentProfile);
  const actionByType: ActionByType =
    userGroup === UserGroup.MARINERS
      ? ActionByType.Mariner
      : ActionByType.Crewing;
  const {
      data: marinerProfileFromApi,
      isLoading,
      isFetching,
      refetch,
    } = useGetMarinerQuery(id !== undefined && id !== "new" ? id : skipToken, {
      refetchOnMountOrArgChange: true,
    });
  const hasComplianceTrainingView =
    userGroup === UserGroup.CREWING_SPECIALISTS ||
    userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
    userGroup === UserGroup.CREWING_MANAGERS ||
    userGroup === UserGroup.CREWING_EXECS ||
    userGroup === UserGroup.FLEET_EXECS ||
    userGroup === UserGroup.VESSEL_EXECS ||
    userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
    !!marinerProfileFromApi?.captainView ||
    (userGroup === UserGroup.MARINERS && userProfile?.id?.toString() === id);
  const isViewOnly =
    userGroup === UserGroup.CREWING_EXECS ||
    userGroup === UserGroup.HUMAN_RESOURCES ||
    (userGroup === UserGroup.MARINERS &&
      userProfile?.id?.toString() !== id &&
      !marinerProfileFromApi?.captainView);
  const hasEvaluationView =
    userGroup === UserGroup.HUMAN_RESOURCES ||
    userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
    userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
    userGroup === UserGroup.CREWING_MANAGERS||
    (userGroup === UserGroup.MARINERS && userProfile?.id?.toString() === id) ||
    marinerProfileFromApi?.captainView;
  
    const hasTrainingView = !marinerProfileFromApi?.captainView;

  const { data: jobOrdersFromApi } = useGetJobOrdersQuery(
    {
      assigneeId: "",
      vesselId: "",
      port: "",
      rating: undefined,
      compatibleWithRating: undefined,
      jobOrderID: "",
      fillByAfterDate: undefined,
      fillByBeforeDate: undefined,
      completed: undefined,
      notApplicable: undefined,
      completedOrNotApplicable: undefined,
      deleted: false,
      archived: undefined,
      critical: undefined,
      newJobOrder: undefined,
      publishStatuses: [],
      smis: undefined,
      profileId: id,
      page: 0,
      size: 15,
      sortColumn: "fillByDate",
      sortDirection: "desc",
    },
    {
      skip:
        id === undefined ||
        isNaN(+id) ||
        (userGroup !== UserGroup.ADMINISTRATORS &&
          userGroup !== UserGroup.CREWING_EXECS &&
          userGroup !== UserGroup.CREWING_MANAGERS &&
          userGroup !== UserGroup.CREWING_SPECIALISTS &&
          userGroup !== UserGroup.CREWING_EVAL_SPECIALISTS &&
          userGroup !== UserGroup.HUMAN_RESOURCES &&
          userGroup !== UserGroup.HUMAN_RESOURCES_ADMINS),
      refetchOnMountOrArgChange: true,
    }
  );

  const handleDocumentsCallback = (
    operationType: ProfileDocumentOperationType
  ) => {
    if (operationType !== ProfileDocumentOperationType.LOAD) {
      toast.success(
        <p>
          Mariner Profile document {operationType.toLowerCase()} successful.
        </p>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
    }
  };

  const handleTrainingDocumentsCallback = (
    operationType: TrainingDocumentOperationType
  ) => {
    if (operationType !== TrainingDocumentOperationType.LOAD) {
      toast.success(
        <p>
          Mariner Profile Training document {operationType.toLowerCase()}{" "}
          successful.
        </p>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
    }
  };

  if (isLoading || (user?.userGroup === UserGroup.MARINERS && !userProfile)) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <MarinerProfileForm 
        marinerProfile={marinerProfileFromApi}
        isLoading={isLoading}
        isFetching = {isFetching}
        refetch={refetch} 
      />
      {id !== undefined && !isNaN(+id) ? (
        <>
          <Paper>
            <h3 className="mb-0">Documents</h3>
            <ProfileDocuments
              profileId={+id}
              parentCallback={handleDocumentsCallback}
              actionByType={actionByType}
              isViewOnly={isViewOnly}
            />
          </Paper>
          <Paper style={{ marginTop: "20px" }}>
            <ProfileTrainingDocuments
              profileId={+id}
              parentCallback={handleTrainingDocumentsCallback}
              actionByType={actionByType}
              isViewOnly={isViewOnly}
            />
          </Paper>
          {hasComplianceTrainingView && <Paper style={{ marginTop: "20px" }}>
            <ProfileComplianceTrainingDocuments profileId={+id} isViewOnly={isViewOnly} />
          </Paper>}
          {hasTrainingView && <Paper style={{ marginTop: "20px" }}>
            <ProfileTrainings
              profileId={+id}
              isViewOnly={isViewOnly}
              actionByType={actionByType}
            />
          </Paper>}
          {hasEvaluationView && (
            <Paper style={{ marginTop: "20px" }}>
              <ProfileEvaluations />
            </Paper>
          )}
          {(jobOrdersFromApi?.totalElements as number) > 0 && (
            <Paper style={{ marginTop: "20px" }}>
              <h3 className="mb-1">Job Order History</h3>
              <TableContainer>
                <Table className="documents-table">
                  <TableHead>
                    <TableRow className="bg-medgrey">
                      <TableCell className="wd-2 py-0">
                        <h5 className="b-600">Job Order ID</h5>
                      </TableCell>
                      <TableCell className="wd-2 py-0">
                        <h5 className="b-600">SMIS #</h5>
                      </TableCell>
                      <TableCell className="wd-2 py-0"></TableCell>
                      <TableCell className="wd-2 py-0"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(jobOrdersFromApi?.content as JobOrder[]).map((row) => {
                      return (
                        <TableRow>
                          <TableCell>
                            <Link
                              className="blue-link justify-content-start"
                              to={`/jobOrders/${row.id}`}
                            >
                              {row.id}
                            </Link>
                          </TableCell>
                          <TableCell>{row.smis}</TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      ) : (
        <Paper className="fileUpload placeholder">
          <FileUploadIcon />
          <p>Save this Mariner profile to begin uploading documents</p>
        </Paper>
      )}
    </>
  );
};

export default MarinerProfileComponent;
